/**
 * 🔔 네이버웍스 봇 테스트 메시지 (v2.157.0 · 2026-09-10)
 *
 * 사용자 지시: 「딜등록 만료 안내가 재고관리봇으로 왔다. 딜등록관리봇을 지금 활성화했으니
 *   테스트 메시지를 한 번 보내보자 — **나한테만**.」
 *
 * ⭐받는 사람은 **로그인한 본인 고정**이다(서버가 세션에서 정한다). 화면에 받는 사람 칸이 없다.
 * ⭐봇 ID 가 서버에 설정되지 않았으면 조용히 기본 봇으로 나간다 — 그 사실을 **빨갛게 알려 준다**.
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, ApiCallError } from '../lib/api';
import { SegmentedControl } from './ui/SegmentedControl';

type BotKind = 'deal' | 'approval' | 'release' | 'manufacture' | 'default';

type BotTestResult = {
  sentTo: string;
  bot: BotKind;
  botLabel: string;
  botId: string | null;
  envKey: string | null;
  usingFallback: boolean;
};

const CHOICES: { key: BotKind; label: string; hint: string }[] = [
  { key: 'deal', label: '딜등록 봇', hint: '딜등록 만료 아침 알림을 보내는 봇' },
  { key: 'approval', label: '결재 알림 봇', hint: '결재 요청·승인 알림을 보내는 봇' },
  // v2.220.0 — 입출고 메시지방 알림을 봇별로 나눔(규칙: 백엔드 bot.ts botIdForStockRoom)
  { key: 'release', label: '출고안내 봇', hint: '입출고 방에 출고신청 접수를 알리는 봇' },
  { key: 'manufacture', label: '제조처리 봇', hint: '입출고 방에 제조신청·제조처리·제조분해를 알리는 봇' },
  { key: 'default', label: '기본 봇 (재고관리)', hint: '그 밖의 재고 알림(회수·반품·주간 재고 확인 등)을 보내는 봇' },
];

export function BotTestCard() {
  const [bot, setBot] = useState<BotKind>('deal');
  const [result, setResult] = useState<BotTestResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const sendMut = useMutation({
    mutationFn: () => api.post<BotTestResult>('/admin/bot-test', { bot }),
    onSuccess: (r) => { setResult(r); setError(null); },
    onError: (e: Error) => {
      setError(e instanceof ApiCallError ? e.message : '발송 실패');
      setResult(null);
    },
  });

  return (
    <div className="bg-white rounded shadow-sm border border-slate-200 p-4">
      <h2 className="ui-section-title mb-1">🔔 봇 테스트 메시지 (네이버웍스)</h2>
      <p className="text-xs text-slate-500 mb-3">
        고른 봇으로 <strong>나에게만</strong> 1:1 테스트 메시지를 보냅니다. 도착한 메시지의{' '}
        <strong>보낸 봇 이름</strong>을 확인하세요 — 알림이 엉뚱한 봇으로 나가는지 이걸로 가려냅니다.
      </p>

      <div className="mb-3">
        <SegmentedControl ariaLabel="시험할 봇" value={bot} onChange={(k) => { setBot(k); setResult(null); setError(null); }}
          options={CHOICES.map((c) => ({ key: c.key, label: c.label, title: c.hint }))} />
      </div>

      <button
        type="button"
        onClick={() => sendMut.mutate()}
        disabled={sendMut.isPending}
        className="btn btn-primary"
      >
        {sendMut.isPending ? '보내는 중…' : '나에게 테스트 메시지 보내기'}
      </button>

      {error && (
        <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
          {error}
        </div>
      )}

      {result && (
        <div className="mt-3 text-sm">
          <div className="text-slate-700">
            <strong>{result.botLabel}</strong> 로 <strong>{result.sentTo}</strong> 에게 보냈습니다.
            {result.botId && <span className="text-slate-500"> (Bot ID {result.botId})</span>}
          </div>
          {result.usingFallback && (
            <div className="mt-2 text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
              ⚠ <strong>{result.botLabel}의 ID가 서버에 설정되어 있지 않습니다.</strong>
              <br />
              그래서 <strong>기본 봇(재고관리)</strong>으로 나갔습니다 — 이 봇이 맡은 알림도 지금 같은 이유로
              재고관리봇에서 오고 있습니다.
              <br />
              서버 <code className="bg-white px-1 rounded border">backend/.env</code> 에{' '}
              <code className="bg-white px-1 rounded border">{result.envKey}=봇ID</code> 를 넣고 재배포하면
              해당 봇으로 나갑니다.
            </div>
          )}
          {!result.usingFallback && (
            <div className="mt-2 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">
              ✅ 지정한 봇 ID로 보냈습니다. 웍스에서 <strong>보낸 봇 이름</strong>이 「{result.botLabel}」인지
              확인해 주세요.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
